import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { api, getAccessToken, errMsg as sharedErrMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { uploadDocumentOnly, UploadZone } from "../lib/resumeUpload.js";
import { HR_ROLES, MGR_ROLES } from "@hrms/types";

const WFH_INCLUDED_DAYS = 5; // mirrors WFH_INCLUDED_DAYS in apps/api/src/modules/leave/leave.service.ts

function getUserId(): string {
  try {
    const token = getAccessToken() ?? "";
    return String((JSON.parse(atob(token.split(".")[1]!)) as Record<string, unknown>)["sub"] ?? "");
  } catch { return ""; }
}
import { useLookup } from "../context/LookupContext.js";
import { useMe } from "../context/MeContext.js";
import { useLeaveTypeConfig, type LeaveTypeConfig } from "../context/LeaveTypeConfigContext.js";
import { ManagedInADP } from "../components/ManagedInADP.js";
import { LeaveInsightCards, type LeaveBalanceRow } from "../components/LeaveInsightCards.js";
import { availableDays } from "../lib/leaveBalance.js";
import { LeaveTypePicker } from "../components/LeaveTypePicker.js";
import { Paperclip, CheckCircle2, Download, ChevronDown, RefreshCw } from "lucide-react";
import { LeaveDatePicker } from "../components/LeaveDatePicker.js";
import { PendingApprovalsQueue } from "../components/PendingApprovalsQueue.js";
import { TeamLeaveCalendar } from "../components/TeamLeaveCalendar.js";
import { LeavePageSkeleton } from "../components/leave/LeavePageSkeleton.js";
import { AsyncButton } from "../components/ui/async-button.js";
import { Button } from "../components/ui/button.js";
import { SearchInput } from "../components/ui/search-input.js";
import { useDebouncedSearch } from "../components/reports/useReport.js";
import { Pagination, type PaginationMeta } from "../components/ui/pagination.js";
import { useToast } from "../context/ToastContext.js";
import { usePageBack } from "../context/PageBackContext.js";
import { DropdownButton, DropdownPanel, DropdownCheckboxRow, DropdownSelect, useCloseOnOutsideClick } from "../components/DropdownButton.js";
import { MenuList } from "../components/ui/menu-list.js";
import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogTitle } from "../components/ui/dialog.js";
import { Textarea } from "../components/ui/textarea.js";
import { REASON_MAX_WORDS, limitWords, wordCount } from "../lib/words.js";
import { useCalendarTodos } from "../lib/useCalendarTodos.js";

interface LeaveRequest {
  id: string;
  leaveType: string;
  startDate: string;
  endDate: string;
  days: number | string;
  halfDay: boolean;
  halfDayPeriod: string | null;
  reason: string | null;
  status: string;
  isWfh: boolean;
  isLop: boolean;
  lopDays: number | string | null;
  lateFlag: boolean;
  isRetroactive: boolean;
  createdAt: string;
  employeeId: string;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  rejectionReason?: string | null;
  attachmentDocumentId: string | null;
}

function calcWorkingDays(start: string, end: string, holidays: Set<string> = new Set()): number {
  if (!start || !end) return 0;
  let count = 0;
  const cur  = new Date(start + "T00:00:00");
  const last = new Date(end   + "T00:00:00");
  while (cur <= last) {
    const d       = cur.getDay();
    const dateStr = cur.toISOString().split("T")[0]!;
    if (d !== 0 && d !== 6 && !holidays.has(dateStr)) count++;
    cur.setDate(cur.getDate() + 1);
  }
  return count;
}

function availableFor(balance: LeaveBalanceRow[], leaveType: string): number | null {
  const b = balance.find((x) => x.leaveType === leaveType);
  if (!b) return null;
  return availableDays(b);
}

function errMsg(e: unknown): string {
  return sharedErrMsg(e) ?? "Something went wrong";
}

function csvRow(values: (string | number | boolean | null | undefined)[]): string {
  return values.map(v => {
    const s = String(v ?? "");
    return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",");
}

function fmtDate(s: string) {
  return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtDateShort(s: string) {
  return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
}

const statusStyle = (s: string): React.CSSProperties => {
  if (s === "APPROVED")  return { background: "var(--color-success-bg)", color: "var(--color-success-fg)" };
  if (s === "REJECTED")  return { background: "var(--color-error-bg)", color: "var(--color-error-fg)" };
  if (s === "PENDING")   return { background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" };
  if (s === "CANCELLED") return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
  return { background: "var(--color-surface-100)", color: "var(--color-text-secondary)" };
};

const pill: React.CSSProperties = {
  display: "inline-block", padding: "1px 7px", borderRadius: 999, fontSize: 10, fontWeight: 700,
};

const sLabel: React.CSSProperties = {
  fontSize: 12, fontWeight: 600, color: "var(--color-surface-600)", marginBottom: 4, display: "block",
};

const sInp: React.CSSProperties = {
  width: "100%", padding: "8px 10px", border: "1.5px solid var(--color-surface-200)",
  borderRadius: "var(--radius-md)", fontSize: 13, outline: "none",
  boxSizing: "border-box", background: "var(--color-surface-0)",
};

const FILTER_STATUSES = ["", "PENDING", "APPROVED", "REJECTED", "CANCELLED"] as const;

function FilterPills({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
      {FILTER_STATUSES.map((s) => (
        <button
          key={s}
          onClick={() => onChange(s)}
          style={{
            padding: "5px 13px", border: "none", borderRadius: "var(--radius-md)",
            cursor: "pointer", fontSize: 12, fontWeight: 500,
            background: value === s ? "var(--color-brand-600)" : "var(--color-surface-0)",
            color: value === s ? "#fff" : "var(--color-surface-700)",
            boxShadow: "var(--shadow-sm)",
          }}
        >
          {s || "All"}
        </button>
      ))}
    </div>
  );
}

function RequestsTable({
  rows,
  canCancel,
  onCancel,
  onEdit,
  canRevoke,
  onRevoke,
}: {
  rows: LeaveRequest[];
  canCancel: boolean;
  onCancel: (id: string) => void;
  onEdit: (r: LeaveRequest) => void;
  canRevoke?: boolean;
  onRevoke?: (id: string) => void;
}) {
  const navigate = useNavigate();
  // Paged in the browser over the rows already loaded; the page is clamped when a
  // filter or an action shrinks the list.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  async function viewAttachment(docId: string) {
    try {
      const r = await api.get<{ url: string }>(`/documents/${docId}/url`);
      window.open(r.data.url, "_blank");
    } catch (e) { alert(errMsg(e)); }
  }

  return (
    <div style={{ background: "var(--color-surface-0)", borderRadius: "var(--radius-lg)", border: "1px solid var(--color-surface-200)", overflow: "hidden" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ background: "var(--color-surface-50)", borderBottom: "1px solid var(--color-surface-200)" }}>
            {["Employee", "Type", "Dates", "Days", "Flags", "Status", ""].map((h) => (
              <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: "0.5px" }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={7} style={{ padding: "36px 14px", textAlign: "center", color: "var(--color-surface-400)", fontSize: 13 }}>No requests found</td></tr>
          ) : pageRows.map((r, i) => (
            <tr key={r.id} style={{ borderBottom: i < pageRows.length - 1 ? "1px solid var(--color-surface-100)" : "none" }}>
              <td style={{ padding: "10px 14px" }}>
                <div
                  style={{ fontWeight: 600, fontSize: 13, cursor: "pointer" }}
                  onClick={() => navigate(`/employees/${r.employee.id}/edit`)}
                  onMouseEnter={e => (e.currentTarget.style.textDecoration = "underline")}
                  onMouseLeave={e => (e.currentTarget.style.textDecoration = "none")}
                >{r.employee.firstName} {r.employee.lastName}</div>
                <div style={{ fontSize: 11, color: "var(--color-surface-400)" }}>{r.employee.employeeCode}</div>
              </td>
              <td style={{ padding: "10px 14px", fontSize: 13, fontWeight: 500 }}>
                {r.leaveType.replace(/_/g, " ")}
                {r.halfDay && <span style={{ fontSize: 10, marginLeft: 4, color: "var(--color-surface-500)" }}>½</span>}
              </td>
              <td style={{ padding: "10px 14px", fontSize: 12, color: "var(--color-surface-700)" }}>
                {r.startDate === r.endDate ? fmtDate(r.startDate) : `${fmtDateShort(r.startDate)} – ${fmtDate(r.endDate)}`}
              </td>
              <td style={{ padding: "10px 14px", fontSize: 13 }}>
                {Number(r.days) % 1 === 0 ? Number(r.days) : Number(r.days).toFixed(1)}
                {r.isLop && r.lopDays && (
                  <div style={{ fontSize: 10, color: "var(--color-error)" }}>{Number(r.lopDays)} LOP</div>
                )}
              </td>
              <td style={{ padding: "10px 14px" }}>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {r.isRetroactive && <span style={{ ...pill, background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" }}>RETRO</span>}
                  {r.lateFlag && !r.isRetroactive && <span style={{ ...pill, background: "var(--color-warning-bg)", color: "var(--color-warning-fg)" }}>LATE</span>}
                  {r.isLop && <span style={{ ...pill, background: "var(--color-error-bg)", color: "var(--color-error-fg)" }}>LOP</span>}
                </div>
              </td>
              <td style={{ padding: "10px 14px" }}>
                <span style={{ ...statusStyle(r.status), padding: "2px 8px", borderRadius: "var(--radius-full)", fontSize: 11, fontWeight: 600 }}>
                  {r.status}
                </span>
                {r.rejectionReason && (
                  <div style={{ fontSize: 11, color: "var(--color-error-fg)", marginTop: 2, fontStyle: "italic", maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={r.rejectionReason}>
                    {r.rejectionReason}
                  </div>
                )}
                {r.attachmentDocumentId && (
                  <button
                    onClick={() => viewAttachment(r.attachmentDocumentId!)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 4, marginTop: 2, background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, color: "var(--color-brand-600)", textDecoration: "underline" }}
                  >
                    <Paperclip size={12} /> Attachment
                  </button>
                )}
              </td>
              <td style={{ padding: "10px 14px" }}>
                {canCancel && r.status === "PENDING" && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button
                      onClick={() => onEdit(r)}
                      style={{ padding: "3px 10px", background: "var(--color-surface-100)", color: "var(--color-surface-600)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => onCancel(r.id)}
                      style={{ padding: "3px 10px", background: "var(--color-surface-100)", color: "var(--color-surface-600)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
                {canRevoke && r.status === "APPROVED" && (
                  <button
                    onClick={() => onRevoke?.(r.id)}
                    style={{ padding: "3px 10px", background: "var(--color-error-bg)", color: "var(--color-error-fg)", border: "none", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
                  >
                    Revoke
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        className="border-t border-surface-200 px-3.5 py-2.5"
        meta={{ total: rows.length, page: currentPage, limit: pageSize, totalPages }}
        onPageChange={setPage}
        onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        pageSizeOptions={[10, 25, 50]}
      />
    </div>
  );
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-surface-500)", textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 10 }}>
      {children}
    </div>
  );
}

// Mirrors validateLeaveRequest's rule order in leave.service.ts (gender -> marital
// status -> probation -> notice period -> PIP -> WFH department) so this pre-submit
// warning never disagrees with what the server will actually enforce.
function getRestrictionMessage(
  cfg: LeaveTypeConfig | undefined,
  leaveType: string,
  me: { gender: string | null; maritalStatus: string | null; status: string; isOnProbation: boolean; isOnActivePip: boolean; departmentWfhEligible: boolean | null }
): string | null {
  if (!cfg) return null;
  if (cfg.genderRestriction && me.gender?.toLowerCase() !== cfg.genderRestriction.toLowerCase()) {
    return `${leaveType} leave is restricted to ${cfg.genderRestriction} employees.`;
  }
  if (cfg.maritalStatusRestriction && me.maritalStatus?.toLowerCase() !== cfg.maritalStatusRestriction.toLowerCase()) {
    return `${leaveType} leave is restricted to ${cfg.maritalStatusRestriction} employees.`;
  }
  if (cfg.isProbationBlocked && me.isOnProbation) {
    return "Leave is not permitted during your probation period.";
  }
  if (cfg.isNoticeBlocked && me.status === "NOTICE_PERIOD") {
    return `${leaveType} leave is not permitted while serving notice period.`;
  }
  if (cfg.isPipBlocked && me.isOnActivePip) {
    return `${leaveType} leave is not permitted at this time. Contact HR for details.`;
  }
  if (leaveType === "WFH" && me.departmentWfhEligible === false) {
    return "WFH is not available for your department.";
  }
  return null;
}

// Mirrors the half-day-aware overlap check in applyLeave/editLeaveRequest (leave.service.ts)
// so a conflict the server will reject shows up before the user submits.
function findOverlapConflict(
  myRequests: LeaveRequest[], startDate: string, endDate: string, halfDay: boolean, halfDayPeriod: string, editingId: string | null
): boolean {
  if (!startDate || !endDate) return false;
  return myRequests.some((r) => {
    if (r.id === editingId) return false;
    if (!["PENDING", "APPROVED"].includes(r.status)) return false;
    const rStart = r.startDate.slice(0, 10);
    const rEnd   = r.endDate.slice(0, 10);
    if (!(rStart <= endDate && rEnd >= startDate)) return false;
    const isComplementaryHalfDay = halfDay && r.halfDay && rStart === startDate && r.halfDayPeriod !== halfDayPeriod;
    return !isComplementaryHalfDay;
  });
}

// Walks every page of /leave/requests for lists that must be complete rather than
// paged: the approvals queue, the overlap check and the CSV export.
async function fetchAllLeaveRequests(params: string): Promise<LeaveRequest[]> {
  const url = (page: number) => `/leave/requests?${params}&limit=100&page=${page}`;
  const first = await api.get<{ data: LeaveRequest[]; meta: PaginationMeta }>(url(1));
  const rest = await Promise.all(
    Array.from({ length: Math.max(0, first.data.meta.totalPages - 1) }, (_, i) =>
      api.get<{ data: LeaveRequest[] }>(url(i + 2))
    )
  );
  return first.data.data.concat(...rest.map(r => r.data.data));
}

const PAGE_SIZE = 20;
const EMPTY_META: PaginationMeta = { total: 0, page: 1, limit: PAGE_SIZE, totalPages: 1 };

// Mirrors the minNoticeDays lateFlag check in leave.service.ts (informational only -- the
// backend still sets lateFlag server-side regardless of what this shows).
function isShortNotice(today: string, startDate: string, minNoticeDays: number | null): boolean {
  if (!minNoticeDays || minNoticeDays <= 0 || !startDate) return false;
  const cutoff = new Date(today + "T00:00:00");
  cutoff.setDate(cutoff.getDate() + minNoticeDays);
  return new Date(startDate + "T00:00:00") < cutoff;
}

export default function LeavePage() {
  usePageBack("/dashboard");
  const { isUS, role, employeeId, gender, maritalStatus, status: employeeStatus, isOnProbation, isOnActivePip, departmentWfhEligible } = useMe();
  const leaveTypes    = useLookup("LEAVE_TYPE");
  const typeConfig    = useLeaveTypeConfig();
  // Admin-disabled types are removed from Apply, the balance cards and the calendar filter;
  // an edit keeps showing its own type.
  const applyTypes    = leaveTypes.filter(t => typeConfig[t.value]?.isEnabled !== false);
  const toast         = useToast();

  const isHR       = HR_ROLES.has(role);
  const isMgr      = MGR_ROLES.has(role);
  const canApprove = isHR || isMgr;
  const PREFS_KEY  = `hrms_prefs_leave_${getUserId()}`;

  // Complete (unpaged) lists: my own PENDING + APPROVED requests for the overlap check,
  // and every PENDING request in scope for the approvals queue.
  const [ownActive,  setOwnActive]  = useState<LeaveRequest[]>([]);
  const [pendingAll, setPendingAll] = useState<LeaveRequest[]>([]);
  // One server page each for the "My Requests" and "All Team Requests" tables.
  const [myRows,    setMyRows]    = useState<LeaveRequest[]>([]);
  const [myMeta,    setMyMeta]    = useState<PaginationMeta>(EMPTY_META);
  const [myPage,    setMyPage]    = useState(1);
  const [teamRows,  setTeamRows]  = useState<LeaveRequest[]>([]);
  const [teamMeta,  setTeamMeta]  = useState<PaginationMeta>(EMPTY_META);
  const [teamPage,  setTeamPage]  = useState(1);
  // Bumped by refresh() so the paged tables refetch after any create/cancel/approve.
  const [refreshKey, setRefreshKey] = useState(0);
  const [balance,  setBalance]  = useState<LeaveBalanceRow[]>([]);
  const [teamBalances, setTeamBalances] = useState<Record<string, LeaveBalanceRow[]>>({});
  const [loading,  setLoading]  = useState(true);
  const [holidays, setHolidays] = useState<Set<string>>(new Set());

  // Deep link from the dashboard "My Todos" widget: ?date=YYYY-MM-DD[&todo=<id>] opens
  // the Calendar on that day (and that todo's detail card).
  const [searchParams, setSearchParams] = useSearchParams();
  const [deepLink, setDeepLink] = useState<{ date: string; todoId?: string } | null>(() => {
    const date = searchParams.get("date");
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
    const todo = searchParams.get("todo");
    return todo ? { date, todoId: todo } : { date };
  });
  const [tab,        setTab]        = useState<"my" | "calendar" | "team">(() => {
    if (deepLink) return "calendar";
    try {
      const d = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).tab;
      return d === "my" || d === "calendar" || d === "team" ? d : "my";
    } catch { return "my"; }
  });
  const [myFilter,   setMyFilter]   = useState<string>(() => {
    try { return String((JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).myFilter ?? ""); }
    catch { return ""; }
  });
  const [teamFilter, setTeamFilter] = useState<string>(() => {
    try { return String((JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).teamFilter ?? ""); }
    catch { return ""; }
  });
  // Team-tab search boxes. The approvals queue is filtered in the browser (it is fully
  // loaded); the "All Team Requests" table searches on the server so every page is covered.
  const [pendingSearch, setPendingSearch] = useState("");
  const [teamSearch, setTeamSearch] = useState("");
  const [teamSearchText, setTeamSearchText] = useDebouncedSearch(teamSearch, (v) => { setTeamSearch(v); setTeamPage(1); });
  const [hideBalance, setHideBalance] = useState<boolean>(() => {
    try { return (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).hideBalance === true; }
    catch { return false; }
  });

  const [viewMode, setViewMode] = useState<"table" | "calendar">("table");
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = deepLink ? new Date(`${deepLink.date}T00:00:00`) : new Date();
    return { year: d.getFullYear(), month: d.getMonth() };
  });
  // Unfiltered month fetch, shared by both calendar views -- see myCalendarRequests /
  // teamCalendarRequests below, which each derive their own slice from this one fetch.
  const [calendarRequests, setCalendarRequests] = useState<LeaveRequest[]>([]);
  const [calendarHolidays, setCalendarHolidays] = useState<{ date: string; name: string; isOptional: boolean; isNational: boolean }[]>([]);

  // Leave types to hide as chips on My Calendar. Empty = show everything, so a
  // leave type added later never needs a migration of stored prefs.
  const [calendarHiddenTypes, setCalendarHiddenTypes] = useState<Set<string>>(() => {
    try {
      const stored = (JSON.parse(localStorage.getItem(PREFS_KEY) ?? "{}") as Record<string, unknown>).calendarHiddenTypes;
      return new Set(Array.isArray(stored) ? (stored as string[]) : []);
    } catch { return new Set(); }
  });
  const [typeFilterOpen, setTypeFilterOpen] = useState(false);

  // Private, self-only todos -- only ever rendered on "My Calendar", never Team.
  const calendarTodos = useCalendarTodos({
    year: calendarMonth.year, month: calendarMonth.month, enabled: tab === "calendar",
    onWriteError: (m) => toast.show(m, "error"),
  });

  const [showDrawer,    setShowDrawer]    = useState(false);
  const [editingId,     setEditingId]     = useState<string | null>(null);
  const [leaveType,     setLeaveType]     = useState(leaveTypes[0]?.value ?? "ANNUAL");
  const [startDate,     setStartDate]     = useState("");
  const [endDate,       setEndDate]       = useState("");
  const [halfDay,       setHalfDay]       = useState(false);
  const [halfDayPeriod, setHalfDayPeriod] = useState<"MORNING" | "AFTERNOON" | "">("");
  const [reason,        setReason]        = useState("");
  const [lopEnabled,    setLopEnabled]    = useState(false);
  const [attachmentDocId, setAttachmentDocId] = useState<string | null>(null);
  const [attachmentName,  setAttachmentName]  = useState("");
  const [saving,        setSaving]        = useState(false);
  const [formErr,       setFormErr]       = useState("");
  // Set only when editing someone else's request (HR acting on a Team-tab row) -- the
  // LOP/available-days preview must reflect the real owner's balance, not the acting
  // HR admin's own.
  const [editingOwner,        setEditingOwner]        = useState<{ id: string; name: string } | null>(null);
  const [editingOwnerBalance, setEditingOwnerBalance] = useState<LeaveBalanceRow[] | null>(null);

  const today = new Date().toISOString().split("T")[0]!;
  const effectiveEnd        = (leaveType === "COMP_OFF" || halfDay) ? startDate : endDate;
  const computedDays        = halfDay ? 0.5 : calcWorkingDays(startDate, effectiveEnd, holidays);
  const isRetroactive       = !!startDate && startDate < today;
  const available           = availableFor(editingOwnerBalance ?? balance, leaveType);
  const effectiveAvailable  = available ?? 0;
  const effectiveAvailableLabel = effectiveAvailable % 1 === 0 ? effectiveAvailable : effectiveAvailable.toFixed(2);
  const needsLOP            = (typeConfig[leaveType]?.trackBalance !== false) && effectiveAvailable < computedDays && computedDays > 0;
  const lopDays             = needsLOP && lopEnabled ? Math.max(0, computedDays - Math.max(0, effectiveAvailable)) : 0;
  const isReasonRequired = isRetroactive || computedDays >= 5;

  const maxDaysPerRequest = typeConfig[leaveType]?.maxDaysPerRequest ?? null;

  const restrictionBlock = getRestrictionMessage(typeConfig[leaveType], leaveType, {
    gender, maritalStatus, status: employeeStatus, isOnProbation, isOnActivePip, departmentWfhEligible,
  });

  // NOTE: intentionally NOT derived from `pendingAll` -- that state is fetched with
  // mine=true, which resolveMyTeamScope() (leave.service.ts) resolves to "self + my
  // direct reports" or "self + everyone in my office," not "just my own requests."
  // Using it here would count colleagues' WFH usage against this employee's own quota.
  // A dedicated, explicitly-scoped fetch (employeeId=<self>) avoids that.
  const [wfhQuota, setWfhQuota] = useState<{ used: number; quarterLabel: string } | null>(null);
  useEffect(() => {
    if (leaveType !== "WFH" || !employeeId) { setWfhQuota(null); return; }
    const ref    = startDate ? new Date(startDate + "T00:00:00") : new Date();
    const q      = Math.floor(ref.getMonth() / 3);
    const qStart = new Date(ref.getFullYear(), q * 3, 1);
    const qEnd   = new Date(ref.getFullYear(), q * 3 + 3, 0);
    const fmt    = (d: Date) => d.toISOString().slice(0, 10);
    let cancelled = false;
    api.get<{ data: { id: string; startDate: string; status: string }[] }>(
      `/leave/requests?mine=true&employeeId=${employeeId}&leaveType=WFH&from=${fmt(qStart)}&limit=10`
    ).then(r => {
      if (cancelled) return;
      const used = r.data.data.filter(req =>
        ["PENDING", "APPROVED"].includes(req.status) && req.id !== editingId
        && new Date(req.startDate) <= qEnd
      ).length;
      setWfhQuota({ used, quarterLabel: `Q${q + 1} ${ref.getFullYear()}` });
    }).catch((err) => logError("LeavePage.loadWfhQuota", err));
    return () => { cancelled = true; };
  }, [leaveType, startDate, employeeId, editingId]);
  const isReasonRecommended = !isReasonRequired && computedDays >= 3;

  // Drives the My Balance WFH card's live "available this quarter" figure -- separate
  // from wfhQuota above, which tracks the form's selected startDate instead of today.
  const [wfhUsage, setWfhUsage] = useState<{ includedUsed: number; extensionUsed: number; quarterLabel: string } | null>(null);
  useEffect(() => {
    if (!departmentWfhEligible || !employeeId) { setWfhUsage(null); return; }
    const ref    = new Date();
    const q      = Math.floor(ref.getMonth() / 3);
    const qStart = new Date(ref.getFullYear(), q * 3, 1);
    const qEnd   = new Date(ref.getFullYear(), q * 3 + 3, 0);
    const fmt    = (d: Date) => d.toISOString().slice(0, 10);
    let cancelled = false;
    api.get<{ data: { startDate: string; status: string; days: string | number }[] }>(
      `/leave/requests?mine=true&employeeId=${employeeId}&leaveType=WFH&from=${fmt(qStart)}&limit=10`
    ).then(r => {
      if (cancelled) return;
      const totalDays = r.data.data
        .filter(req => ["PENDING", "APPROVED"].includes(req.status) && new Date(req.startDate) <= qEnd)
        .reduce((sum, req) => sum + Number(req.days), 0);
      setWfhUsage({
        includedUsed:  Math.min(WFH_INCLUDED_DAYS, totalDays),
        extensionUsed: Math.max(0, totalDays - WFH_INCLUDED_DAYS),
        quarterLabel:  `Q${q + 1} ${ref.getFullYear()}`,
      });
    }).catch((err) => logError("LeavePage.loadWfhUsage", err));
    return () => { cancelled = true; };
  }, [departmentWfhEligible, employeeId]);

  const balanceForCards = useMemo(() => {
    const withWfh = !departmentWfhEligible || balance.some(b => b.leaveType === "WFH")
      ? balance
      : [...balance, { leaveType: "WFH", allocated: 0, used: 0, pending: 0, probationBlocked: isOnProbation }];
    return withWfh.filter(b => typeConfig[b.leaveType]?.isEnabled !== false);
  }, [balance, departmentWfhEligible, isOnProbation, typeConfig]);

  // HR's own pending request belongs in their approvals queue too -- the backend already
  // permits HR to approve/reject any request, including their own (assertApprovalAuthority
  // skips the direct-manager check for HR). Managers are deliberately excluded from this
  // self-inclusion: isDirectManager is never true of yourself, so it would only add a
  // dead-end Approve button that 403s.
  const teamPending = canApprove ? pendingAll.filter((r) => isHR || r.employeeId !== employeeId) : [];

  const myCalendarRequests   = calendarRequests.filter((r) => r.employeeId === employeeId);
  const teamCalendarRequests = calendarRequests.filter((r) => r.employeeId !== employeeId);

  const overlapConflict  = findOverlapConflict(ownActive, startDate, effectiveEnd, halfDay, halfDayPeriod, editingId);
  const minNoticeDays     = typeConfig[leaveType]?.minNoticeDays ?? null;
  const shortNotice       = !isRetroactive && isShortNotice(today, startDate, minNoticeDays);
  const attachmentThreshold = typeConfig[leaveType]?.attachmentRequiredAboveDays ?? null;
  const attachmentRequired  = attachmentThreshold != null && computedDays > attachmentThreshold && !attachmentDocId;

  const [blackoutBlock, setBlackoutBlock] = useState<{ name: string } | null>(null);
  useEffect(() => {
    if (!startDate || !effectiveEnd) { setBlackoutBlock(null); return; }
    let cancelled = false;
    api.get<{ blocked: boolean; name: string | null }>(
      `/leave/blackouts/check?leaveType=${leaveType}&startDate=${startDate}&endDate=${effectiveEnd}`
    ).then(r => { if (!cancelled) setBlackoutBlock(r.data.blocked ? { name: r.data.name ?? "a blackout period" } : null); })
     .catch((err) => logError("LeavePage.loadBlackoutCheck", err));
    return () => { cancelled = true; };
  }, [leaveType, startDate, effectiveEnd]);

  const load = useCallback(async () => {
    // An empty employeeId would turn the employeeId=<self> fetch below org-wide for HR.
    if (!employeeId) return;
    setLoading(true);
    try {
      const year = new Date().getFullYear();
      const [pending, ownApproved, balRes] = await Promise.all([
        fetchAllLeaveRequests("mine=true&status=PENDING"),
        fetchAllLeaveRequests(`mine=true&employeeId=${employeeId}&status=APPROVED`),
        api.get<LeaveBalanceRow[]>("/leave/balance"),
      ]);
      setPendingAll(pending);
      setOwnActive([...pending.filter(r => r.employeeId === employeeId), ...ownApproved]);
      setBalance(balRes.data);
      api.get<{ date: string }[]>(`/admin/holidays?year=${year}`)
        .then(r => setHolidays(new Set(r.data.map(h => h.date.split("T")[0]!))))
        .catch((err) => logError("LeavePage.load.holidays", err));

      const teamPendingIds = canApprove ? [...new Set(
        pending.filter(r => isHR || r.employeeId !== employeeId).map(r => r.employeeId)
      )] : [];
      const balEntries = await Promise.all(teamPendingIds.map(async (id) => {
        try { return [id, (await api.get<LeaveBalanceRow[]>(`/leave/balance/${id}`)).data] as const; }
        catch { return [id, []] as const; }
      }));
      setTeamBalances(Object.fromEntries(balEntries));
    } catch (err) { logError("LeavePage.load", err); } finally { setLoading(false); }
  }, [employeeId, isHR, canApprove]);

  useEffect(() => { void load(); }, [load]);

  // After any create/edit/cancel/approve: reload the queue and refetch both table pages.
  const refresh = useCallback(() => {
    void load();
    setRefreshKey(k => k + 1);
  }, [load]);

  // "My Requests" table: one server page of my own requests.
  useEffect(() => {
    if (!employeeId) return;
    let cancelled = false;
    const status = myFilter ? `&status=${myFilter}` : "";
    api.get<{ data: LeaveRequest[]; meta: PaginationMeta }>(
      `/leave/requests?mine=true&employeeId=${employeeId}&page=${myPage}&limit=${PAGE_SIZE}${status}`
    ).then(r => {
      if (cancelled) return;
      // Cancelling the only row on the last page empties it -- step back a page.
      if (r.data.meta.totalPages > 0 && myPage > r.data.meta.totalPages) { setMyPage(r.data.meta.totalPages); return; }
      setMyRows(r.data.data);
      setMyMeta(r.data.meta);
    }).catch((err) => logError("LeavePage.loadMyPage", err));
    return () => { cancelled = true; };
  }, [employeeId, myPage, myFilter, refreshKey]);

  // "All Team Requests" table: one server page of everyone in scope except me.
  useEffect(() => {
    if (!canApprove) return;
    let cancelled = false;
    const status = teamFilter ? `&status=${teamFilter}` : "";
    const search = teamSearch ? `&search=${encodeURIComponent(teamSearch)}` : "";
    api.get<{ data: LeaveRequest[]; meta: PaginationMeta }>(
      `/leave/requests?mine=true&excludeSelf=true&page=${teamPage}&limit=${PAGE_SIZE}${status}${search}`
    ).then(r => {
      if (cancelled) return;
      if (r.data.meta.totalPages > 0 && teamPage > r.data.meta.totalPages) { setTeamPage(r.data.meta.totalPages); return; }
      setTeamRows(r.data.data);
      setTeamMeta(r.data.meta);
    }).catch((err) => logError("LeavePage.loadTeamPage", err));
    return () => { cancelled = true; };
  }, [canApprove, teamPage, teamFilter, teamSearch, refreshKey]);

  const loadCalendarMonth = useCallback(async () => {
    const monthStart = new Date(calendarMonth.year, calendarMonth.month, 1);
    const monthEnd   = new Date(calendarMonth.year, calendarMonth.month + 1, 0);
    // Local date parts: toISOString() shifts to UTC, which in IST moved the month start
    // back a day and dropped the last day of the month.
    const fmt = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    const range = `status=APPROVED&from=${fmt(monthStart)}&to=${fmt(monthEnd)}&limit=100`;
    try {
      // Own leaves are fetched on their own: the shared "mine" scope also covers
      // reports/office, so a busy month could push the user's own rows past the limit.
      const [ownRes, ownPendingRes, teamRes, holRes] = await Promise.all([
        api.get<{ data: LeaveRequest[] }>(`/leave/requests?mine=true&employeeId=${employeeId}&${range}`),
        // Own pending requests show too (faded, dashed) so a fresh application appears at once.
        api.get<{ data: LeaveRequest[] }>(`/leave/requests?mine=true&employeeId=${employeeId}&${range.replace("APPROVED", "PENDING")}`).catch(() => ({ data: { data: [] as LeaveRequest[] } })),
        api.get<{ data: LeaveRequest[] }>(`/leave/requests?mine=true&excludeSelf=true&${range}`).catch(() => ({ data: { data: [] as LeaveRequest[] } })),
        api.get<{ date: string; name: string; isOptional: boolean; isNational: boolean }[]>(`/admin/holidays?year=${calendarMonth.year}`),
      ]);
      setCalendarRequests([...ownRes.data.data, ...ownPendingRes.data.data, ...teamRes.data.data.filter(r => r.employeeId !== employeeId)]);
      setCalendarHolidays(holRes.data);
    } catch (err) { logError("LeavePage.loadCalendarMonth", err); }
  }, [calendarMonth, employeeId]);

  // Refresh button: refetch the month's leaves and todos in place, no page reload.
  const [calendarRefreshing, setCalendarRefreshing] = useState(false);
  const refreshCalendar = useCallback(async () => {
    setCalendarRefreshing(true);
    try { await Promise.all([loadCalendarMonth(), calendarTodos.reload()]); }
    finally { setCalendarRefreshing(false); }
  }, [loadCalendarMonth, calendarTodos]);

  const [calendarScope, setCalendarScope] = useState<"mine" | "team" | "all">("mine");
  const [scopeOpen, setScopeOpen] = useState(false);
  const typeFilterRef = useRef<HTMLDivElement>(null);
  const closeTypeFilter = useCallback(() => setTypeFilterOpen(false), []);
  useCloseOnOutsideClick(typeFilterRef, typeFilterOpen, closeTypeFilter);

  useEffect(() => {
    if (tab === "calendar" || (tab === "team" && viewMode === "calendar")) void loadCalendarMonth();
  }, [tab, viewMode, calendarMonth, loadCalendarMonth]);

  function toggleCalendarType(leaveTypeValue: string) {
    setCalendarHiddenTypes(prev => {
      const next = new Set(prev);
      if (next.has(leaveTypeValue)) next.delete(leaveTypeValue); else next.add(leaveTypeValue);
      return next;
    });
  }

  const [exporting, setExporting] = useState(false);

  async function exportTeamCsv() {
    setExporting(true);
    try {
      const rows = await fetchAllLeaveRequests(`mine=true&excludeSelf=true${teamFilter ? `&status=${teamFilter}` : ""}${teamSearch ? `&search=${encodeURIComponent(teamSearch)}` : ""}`);
      if (rows.length === 0) { toast.show("No matching requests to export", "info"); return; }
      const csv = [
        csvRow(["Employee", "Employee Code", "Leave Type", "Start Date", "End Date", "Days", "Status", "Reason"]),
        ...rows.map(r => csvRow([
          `${r.employee.firstName} ${r.employee.lastName}`,
          r.employee.employeeCode,
          r.leaveType.replace(/_/g, " "),
          r.startDate.slice(0, 10),
          r.endDate.slice(0, 10),
          Number(r.days) % 1 === 0 ? Number(r.days) : Number(r.days).toFixed(1),
          r.status,
          r.reason ?? "",
        ])),
      ].join("\n");
      const blob = new Blob([csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `team-leave-${teamFilter || "all"}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      logError("LeavePage.exportTeamCsv", err);
      toast.show("Export failed", "error");
    } finally {
      setExporting(false);
    }
  }

  async function exportMyExcel() {
    setExporting(true);
    try {
      const res = await api.get("/leave/my-export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-leave-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      logError("LeavePage.exportMyExcel", err);
      toast.show("Export failed", "error");
    } finally {
      setExporting(false);
    }
  }

  useEffect(() => {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify({
        tab, myFilter, teamFilter, calendarHiddenTypes: [...calendarHiddenTypes], hideBalance,
      }));
    } catch { /* private browsing */ }
  }, [PREFS_KEY, tab, myFilter, teamFilter, calendarHiddenTypes, hideBalance]);

  function openDrawer(existing?: LeaveRequest) {
    setEditingId(existing?.id ?? null);
    setLeaveType(existing?.leaveType ?? leaveTypes[0]?.value ?? "ANNUAL");
    setStartDate(existing?.startDate ?? ""); setEndDate(existing?.endDate ?? "");
    setHalfDay(existing?.halfDay ?? false); setHalfDayPeriod((existing?.halfDayPeriod as "MORNING" | "AFTERNOON") ?? "");
    setReason(existing?.reason ?? ""); setLopEnabled(existing?.isLop ?? false);
    setAttachmentDocId(existing?.attachmentDocumentId ?? null);
    setAttachmentName(existing?.attachmentDocumentId ? "Existing attachment" : "");
    setFormErr("");
    if (existing && existing.employeeId !== employeeId) {
      setEditingOwner({ id: existing.employeeId, name: `${existing.employee.firstName} ${existing.employee.lastName}` });
      api.get<LeaveBalanceRow[]>(`/leave/balance/${existing.employeeId}`)
        .then(r => setEditingOwnerBalance(r.data))
        .catch((err) => { logError("LeavePage.openDrawer.balance", err); setEditingOwnerBalance(null); });
    } else {
      setEditingOwner(null); setEditingOwnerBalance(null);
    }
    setShowDrawer(true);
  }

  async function handleAttachment(file: File) {
    try {
      const { documentId } = await uploadDocumentOnly(employeeId, file, "LEAVE_ATTACHMENT");
      setAttachmentDocId(documentId);
      setAttachmentName(file.name);
    } catch (e) { logError("LeavePage.handleAttachment", e, { employeeId }); setFormErr(errMsg(e)); }
  }

  function closeDrawer() { setShowDrawer(false); setEditingId(null); setEditingOwner(null); setEditingOwnerBalance(null); }

  function handleLeaveTypeChange(t: string) {
    setLeaveType(t);
    setHalfDay(false); setHalfDayPeriod("");
    setLopEnabled(false); setFormErr("");
  }

  async function handleApply() {
    if (!leaveType)   { setFormErr("Select a leave type"); throw new Error("validation"); }
    if (!startDate)   { setFormErr("Select a start date"); throw new Error("validation"); }
    if (leaveType !== "COMP_OFF" && !halfDay && !endDate) { setFormErr("Select an end date"); throw new Error("validation"); }
    if (halfDay && !halfDayPeriod) { setFormErr("Select morning or afternoon for the half day"); throw new Error("validation"); }
    if (isReasonRequired && !reason.trim()) { setFormErr("A reason is required for this request"); throw new Error("validation"); }
    if (needsLOP && !lopEnabled) { setFormErr(`Insufficient balance (${effectiveAvailableLabel} days available). Enable LOP split or choose fewer days`); throw new Error("validation"); }

    const payload: Record<string, unknown> = {
      ...(editingId ? {} : { leaveType }),
      startDate,
      endDate: effectiveEnd || startDate,
      halfDay,
      halfDayPeriod: halfDay ? halfDayPeriod : undefined,
      reason: reason.trim() || undefined,
      isLop: lopEnabled && needsLOP,
      lopDays: lopEnabled && needsLOP ? lopDays : undefined,
      attachmentDocumentId: attachmentDocId ?? undefined,
    };

    setSaving(true); setFormErr("");
    try {
      if (editingId) await api.patch(`/leave/requests/${editingId}`, payload);
      else await api.post("/leave/requests", payload);
      closeDrawer();
      refresh();
    } catch (e) { setFormErr(errMsg(e)); throw e; }
    finally { setSaving(false); }
  }

  async function handleCancel(id: string) {
    try { await api.patch(`/leave/requests/${id}/cancel`); refresh(); }
    catch (e) { alert(errMsg(e)); }
  }

  async function handleRevoke(id: string) {
    const reason = window.prompt("Reason for revoking this approved leave request:");
    if (!reason || !reason.trim()) return;
    try { await api.patch(`/leave/requests/${id}/revoke`, { reason: reason.trim() }); refresh(); }
    catch (e) { alert(errMsg(e)); }
  }

  if (isUS) return <ManagedInADP module="Leave" />;

  return (
    <div style={{ minHeight: "100vh", background: "var(--color-surface-50)" }}>
      {/* Page header */}
      <div style={{
        background: "var(--color-surface-0)",
        padding: "calc(var(--space-5) / 2) var(--space-4)", display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>Leave & WFH</h1>
        </div>
        <button
          onClick={() => openDrawer()}
          style={{ padding: "8px 18px", background: "var(--color-brand-600)", color: "#fff", border: "none", borderRadius: "var(--radius-md)", cursor: "pointer", fontWeight: 600, fontSize: 14 }}
        >
          + Apply Leave
        </button>
      </div>

      {/* Tabs */}
      <div style={{ background: "var(--color-surface-0)", borderBottom: "1px solid var(--color-surface-200)", padding: "0 var(--space-4)", display: "flex", gap: 0, alignItems: "center" }}>
        {(["my", "calendar", ...(canApprove ? ["team"] : [])] as Array<"my" | "calendar" | "team">).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              padding: "12px 20px", background: "none", border: "none",
              borderBottom: tab === t ? "2.5px solid var(--color-brand-600)" : "2.5px solid transparent",
              cursor: "pointer", fontSize: 14, fontWeight: tab === t ? 700 : 500,
              color: tab === t ? "var(--color-brand-600)" : "var(--color-surface-600)",
              transition: "color 0.1s",
            }}
          >
            {t === "my" ? "My Leave" : t === "calendar" ? "Calendar" : (
              <>
                Team
                {teamPending.length > 0 && !loading && (
                  <span style={{ marginLeft: 6, background: "var(--color-error)", color: "#fff", borderRadius: 99, fontSize: 10, fontWeight: 700, padding: "1px 6px" }}>
                    {teamPending.length}
                  </span>
                )}
              </>
            )}
          </button>
        ))}
        {(myFilter || teamFilter) && (
          <button onClick={() => {
            try { localStorage.removeItem(PREFS_KEY); } catch { /* */ }
            setMyFilter(""); setTeamFilter(""); setMyPage(1); setTeamPage(1);
          }} style={{ marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--color-brand-600)", fontWeight: 600, padding: "4px 8px" }}>
            Reset filters
          </button>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <LeavePageSkeleton tab={tab} />
      ) : (
        <div style={{ padding: "calc(var(--space-6) / 2) var(--space-4)" }}>

          {/* MY LEAVE TAB */}
          {tab === "my" && (
            <div>
              {balanceForCards.length > 0 && (
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                    <SectionHeader>My Balance — {new Date().getFullYear()}</SectionHeader>
                    <button
                      onClick={() => setHideBalance(h => !h)}
                      aria-label={hideBalance ? "Show balance cards" : "Hide balance cards"}
                      aria-expanded={!hideBalance}
                      style={{
                        display: "flex", alignItems: "center", gap: 4, background: "none", border: "none",
                        padding: "2px 4px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: "var(--color-surface-500)",
                      }}
                    >
                      {hideBalance ? "Show" : "Hide"}
                      <ChevronDown size={14} style={{ transform: hideBalance ? "rotate(-90deg)" : "none", transition: "transform 0.15s" }} />
                    </button>
                  </div>
                  {!hideBalance && (
                    <LeaveInsightCards balance={balanceForCards} labels={Object.fromEntries(leaveTypes.map(t => [t.value, t.displayName]))} typeConfig={typeConfig} gender={gender} maritalStatus={maritalStatus} wfhUsage={wfhUsage} />
                  )}
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <SectionHeader>My Requests</SectionHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <FilterPills value={myFilter} onChange={v => { setMyFilter(v); setMyPage(1); }} />
                  <Button variant="outline" size="sm" onClick={() => void exportMyExcel()} disabled={exporting}>
                    <Download size={14} /> Export Excel
                  </Button>
                </div>
              </div>
              <RequestsTable rows={myRows} canCancel={true} onCancel={handleCancel} onEdit={openDrawer} canRevoke={isHR} onRevoke={handleRevoke} />
              <Pagination meta={myMeta} onPageChange={setMyPage} />
            </div>
          )}

          {/* CALENDAR TAB -- visible to every role, unlike Team below */}
          {tab === "calendar" && (
            <div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <SectionHeader>My Calendar</SectionHeader>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => void refreshCalendar()} disabled={calendarRefreshing} aria-label="Refresh calendar">
                    <RefreshCw size={14} className={calendarRefreshing ? "animate-spin" : undefined} /> Refresh
                  </Button>
                  <DropdownSelect
                    value={calendarScope}
                    onChange={v => setCalendarScope(v as "mine" | "team" | "all")}
                    options={[
                      { value: "mine", label: "Only me" },
                      { value: "team", label: "Team only" },
                      { value: "all", label: "Me + Team" },
                    ]}
                    placeholder="Only me"
                    clearable={false}
                    ariaLabel="Whose leave to show"
                    open={scopeOpen}
                    onToggle={() => setScopeOpen(o => !o)}
                    onClose={() => setScopeOpen(false)}
                  />
                  <div ref={typeFilterRef} className="relative">
                    <DropdownButton expanded={typeFilterOpen} onClick={() => setTypeFilterOpen(o => !o)}>
                      {calendarHiddenTypes.size === 0 ? "All Types" : `Types (${applyTypes.filter(t => !calendarHiddenTypes.has(t.value)).length})`}
                    </DropdownButton>
                    {typeFilterOpen && (
                      <DropdownPanel align="end" className="min-w-[180px]">
                        <MenuList className="p-2">
                          {applyTypes.map(t => (
                            <DropdownCheckboxRow key={t.value} checked={!calendarHiddenTypes.has(t.value)} onChange={() => toggleCalendarType(t.value)}>
                              {t.displayName}
                            </DropdownCheckboxRow>
                          ))}
                        </MenuList>
                      </DropdownPanel>
                    )}
                  </div>
                </div>
              </div>
              <TeamLeaveCalendar
                year={calendarMonth.year}
                month={calendarMonth.month}
                requests={[
                  ...(calendarScope !== "team" ? myCalendarRequests : []),
                  ...(calendarScope !== "mine" ? teamCalendarRequests : []),
                ].filter(r => !calendarHiddenTypes.has(r.leaveType))}
                holidays={calendarHolidays}
                showEmployeeName={calendarScope !== "mine"}
                selfId={employeeId}
                deepLink={deepLink}
                onDeepLinkHandled={() => { setDeepLink(null); setSearchParams({}, { replace: true }); }}
                todos={{
                  items: calendarTodos.todos, loading: calendarTodos.loading, loadError: calendarTodos.loadError, onRetry: () => void calendarTodos.reload(),
                  onToggle: calendarTodos.setDone,
                  onSave: async (draft, id) => (id ? (await calendarTodos.update(id, draft), null) : calendarTodos.create(draft)),
                  onDelete: calendarTodos.remove, onRestore: calendarTodos.restore,
                  onMove: calendarTodos.moveTo, onUndoMove: calendarTodos.restoreDates,
                }}
                onPrevMonth={() => setCalendarMonth(m => {
                  const d = new Date(m.year, m.month - 1, 1);
                  return { year: d.getFullYear(), month: d.getMonth() };
                })}
                onNextMonth={() => setCalendarMonth(m => {
                  const d = new Date(m.year, m.month + 1, 1);
                  return { year: d.getFullYear(), month: d.getMonth() };
                })}
                onToday={() => {
                  const d = new Date();
                  setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
                }}
              />
            </div>
          )}

          {/* TEAM TAB */}
          {tab === "team" && canApprove && (
            <div>
              {teamPending.length > 0 && (
                <div style={{ marginBottom: 32 }}>
                  <div className="flex items-center justify-between gap-3">
                    <SectionHeader>Pending Approvals · {teamPending.length}</SectionHeader>
                    <SearchInput
                      className="mb-2.5 w-56" placeholder="Search name or code" aria-label="Search pending approvals"
                      value={pendingSearch} onChange={(e) => setPendingSearch(e.target.value)}
                    />
                  </div>
                  <PendingApprovalsQueue requests={teamPending} onRefresh={refresh} balances={teamBalances} search={pendingSearch} />
                </div>
              )}

              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                <SectionHeader>All Team Requests</SectionHeader>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  {viewMode === "table" && (
                    <SearchInput
                      className="w-56" placeholder="Search name or code" aria-label="Search team requests"
                      value={teamSearchText} onChange={(e) => setTeamSearchText(e.target.value)}
                    />
                  )}
                  {viewMode === "table" && <FilterPills value={teamFilter} onChange={v => { setTeamFilter(v); setTeamPage(1); }} />}
                  <div style={{ display: "flex", gap: 4 }}>
                    <button
                      onClick={() => setViewMode("table")}
                      style={{
                        padding: "4px 12px", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 12, fontWeight: 600, border: "none",
                        background: viewMode === "table" ? "var(--color-brand-600)" : "var(--color-surface-100)",
                        color: viewMode === "table" ? "#fff" : "var(--color-surface-600)",
                      }}
                    >
                      Table
                    </button>
                    <button
                      onClick={() => setViewMode("calendar")}
                      style={{
                        padding: "4px 12px", borderRadius: "var(--radius-sm)", cursor: "pointer", fontSize: 12, fontWeight: 600, border: "none",
                        background: viewMode === "calendar" ? "var(--color-brand-600)" : "var(--color-surface-100)",
                        color: viewMode === "calendar" ? "#fff" : "var(--color-surface-600)",
                      }}
                    >
                      Calendar
                    </button>
                  </div>
                  {viewMode === "calendar" && (
                    <Button variant="outline" size="sm" onClick={() => void refreshCalendar()} disabled={calendarRefreshing} aria-label="Refresh calendar">
                      <RefreshCw size={14} className={calendarRefreshing ? "animate-spin" : undefined} /> Refresh
                    </Button>
                  )}
                  {viewMode === "table" && (
                    <Button variant="outline" size="sm" onClick={() => void exportTeamCsv()} disabled={exporting}>
                      <Download size={14} /> Export CSV
                    </Button>
                  )}
                </div>
              </div>
              {viewMode === "table" ? (
                <>
                  <RequestsTable rows={teamRows} canCancel={isHR} onCancel={handleCancel} onEdit={openDrawer} canRevoke={canApprove} onRevoke={handleRevoke} />
                  <Pagination meta={teamMeta} onPageChange={setTeamPage} />
                </>
              ) : (
                <TeamLeaveCalendar
                  year={calendarMonth.year}
                  month={calendarMonth.month}
                  requests={calendarRequests}
                  holidays={calendarHolidays}
                  selfId={employeeId}
                  onPrevMonth={() => setCalendarMonth(m => {
                    const d = new Date(m.year, m.month - 1, 1);
                    return { year: d.getFullYear(), month: d.getMonth() };
                  })}
                  onNextMonth={() => setCalendarMonth(m => {
                    const d = new Date(m.year, m.month + 1, 1);
                    return { year: d.getFullYear(), month: d.getMonth() };
                  })}
                  onToday={() => {
                    const d = new Date();
                    setCalendarMonth({ year: d.getFullYear(), month: d.getMonth() });
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Apply Leave Drawer */}
      {showDrawer && (
        <>
          <div onClick={closeDrawer} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 40 }} />
          <div style={{
            position: "fixed", top: 0, right: 0, bottom: 0, width: 460,
            background: "var(--color-surface-0)", zIndex: 50,
            display: "flex", flexDirection: "column",
            boxShadow: "-8px 0 32px rgba(0,0,0,0.15)",
          }}>
            {/* Drawer header */}
            <div style={{ padding: "var(--space-5) var(--space-6)", borderBottom: "1px solid var(--color-surface-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h2 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{editingId ? "Edit Leave Request" : "Apply Leave"}</h2>
                {editingOwner && (
                  <div style={{ fontSize: 12, color: "var(--color-surface-500)", marginTop: 2 }}>Editing leave for {editingOwner.name}</div>
                )}
              </div>
              <button onClick={closeDrawer} style={{ background: "none", border: "none", fontSize: 22, cursor: "pointer", color: "var(--color-surface-400)", lineHeight: 1 }}>×</button>
            </div>

            {/* Drawer body */}
            <div style={{ flex: 1, overflowY: "auto", padding: "var(--space-5) var(--space-6)", display: "flex", flexDirection: "column", gap: 20 }}>

              {formErr && (
                <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
                  {formErr}
                </div>
              )}

              {/* Leave type */}
              <div>
                <label style={sLabel}>Leave Type</label>
                <LeaveTypePicker
                  types={editingId ? leaveTypes : applyTypes}
                  balance={balance}
                  value={leaveType}
                  onChange={handleLeaveTypeChange}
                  disabled={!!editingId}
                />
              </div>

              {/* Eligibility restriction (gender/marital status/probation/notice/PIP/WFH-department) */}
              {restrictionBlock && (
                <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
                  {restrictionBlock}
                </div>
              )}

              {/* Date picker */}
              <div>
                <label style={sLabel}>Dates</label>
                <LeaveDatePicker
                  leaveType={leaveType}
                  startDate={startDate}
                  endDate={endDate}
                  halfDay={halfDay}
                  halfDayPeriod={halfDayPeriod}
                  onStartDate={setStartDate}
                  onEndDate={setEndDate}
                  onHalfDay={setHalfDay}
                  onHalfDayPeriod={setHalfDayPeriod}
                />
              </div>

              {/* Overlapping dates with an existing request */}
              {overlapConflict && (
                <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
                  This overlaps with one of your existing leave requests for these dates.
                </div>
              )}

              {/* WFH quarterly allowance */}
              {wfhQuota && (
                <div style={{
                  background: wfhQuota.used >= 1 ? "var(--color-error-bg)" : "var(--color-surface-50)",
                  color:      wfhQuota.used >= 1 ? "var(--color-error-fg)" : "var(--color-surface-600)",
                  borderRadius: 8, padding: "10px 14px", fontSize: 13,
                }}>
                  {wfhQuota.used >= 1
                    ? <>Your WFH allowance for {wfhQuota.quarterLabel} is already used (1 request per quarter). It resets next quarter.</>
                    : <>WFH allowance for {wfhQuota.quarterLabel}: {wfhQuota.used} of 1 used.</>}
                </div>
              )}

              {/* Max days per request */}
              {maxDaysPerRequest != null && (
                <div style={{
                  background: computedDays > maxDaysPerRequest ? "var(--color-error-bg)" : "var(--color-surface-50)",
                  color:      computedDays > maxDaysPerRequest ? "var(--color-error-fg)" : "var(--color-surface-600)",
                  borderRadius: 8, padding: "10px 14px", fontSize: 13,
                }}>
                  {computedDays > maxDaysPerRequest
                    ? <>This request is {computedDays} days, over the {maxDaysPerRequest} day limit for {leaveType} leave. Please split it into multiple requests.</>
                    : <>Maximum {maxDaysPerRequest} day(s) per request for {leaveType} leave.</>}
                </div>
              )}

              {/* Blackout period */}
              {blackoutBlock && (
                <div style={{ background: "var(--color-error-bg)", color: "var(--color-error-fg)", padding: "10px 14px", borderRadius: 8, fontSize: 13 }}>
                  Leave is not permitted during &quot;{blackoutBlock.name}&quot;.
                </div>
              )}

              {/* Retroactive notice */}
              {isRetroactive && (
                <div style={{ background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--color-warning-fg)" }}>
                  <strong>Retroactive request</strong> — this leave date is in the past. Retroactive requests are only allowed up to {typeConfig[leaveType]?.retroactiveWindowDays ?? 30} days back. A reason is required and your manager will be notified this was submitted after the fact.
                </div>
              )}

              {/* Short notice */}
              {shortNotice && (
                <div style={{ background: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: 8, padding: "10px 14px", fontSize: 13, color: "var(--color-warning-fg)" }}>
                  This request gives less than {minNoticeDays} day(s) notice and may be flagged as late.
                </div>
              )}

              {/* LOP prompt */}
              {needsLOP && (
                <div style={{ background: "var(--color-warning-bg-subtle)", border: "1px solid var(--color-warning-border)", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#7c2d12" }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>Insufficient balance</div>
                  <div style={{ marginBottom: 10 }}>
                    You have <strong>{effectiveAvailableLabel} {effectiveAvailable === 1 ? "day" : "days"}</strong> available but requested <strong>{computedDays} {computedDays === 1 ? "day" : "days"}</strong>.
                  </div>
                  <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer" }}>
                    <input
                      type="checkbox"
                      checked={lopEnabled}
                      onChange={(e) => setLopEnabled(e.target.checked)}
                      style={{ marginTop: 1 }}
                    />
                    <div>
                      Apply LOP (Loss of Pay) for the remaining <strong>{computedDays - Math.max(0, effectiveAvailable)} {computedDays - Math.max(0, effectiveAvailable) === 1 ? "day" : "days"}</strong>
                      <div style={{ fontSize: 11, color: "var(--color-warning-fg-strong)", marginTop: 2 }}>
                        {effectiveAvailable} days from balance · {computedDays - Math.max(0, effectiveAvailable)} unpaid. Manager must approve.
                      </div>
                    </div>
                  </label>
                </div>
              )}

              {/* Reason */}
              <div>
                <label style={sLabel}>
                  Reason{isReasonRequired ? " *" : isReasonRecommended ? " (recommended)" : ""}
                </label>
                <textarea
                  rows={3}
                  placeholder={
                    isRetroactive
                      ? "Explain why leave was taken without prior request…"
                      : isReasonRequired
                        ? "Reason for leave (required for requests ≥ 5 days)…"
                        : "Optional — give your manager context"
                  }
                  value={reason}
                  // Typing or pasting past the limit is cut at the 30th word.
                  onChange={(e) => setReason(limitWords(e.target.value))}
                  style={{ ...sInp, resize: "none", height: "auto" }}
                />
                <div className={`mt-1 text-right text-[11px] ${wordCount(reason) >= REASON_MAX_WORDS ? "text-error" : "text-surface-500"}`}>
                  {wordCount(reason)}/{REASON_MAX_WORDS} words
                </div>
              </div>

              {/* Attachment */}
              <div>
                <label style={sLabel}>Attach supporting document{attachmentRequired ? " *" : " (optional)"}</label>
                <UploadZone onFile={handleAttachment} label={attachmentRequired ? "Attach supporting document (required)" : "Attach supporting document (optional)"} />
                {attachmentName && <p style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, color: "var(--color-surface-500)", marginTop: 6 }}><CheckCircle2 size={12} /> {attachmentName}</p>}
                {attachmentRequired && (
                  <p style={{ fontSize: 12, color: "var(--color-error-fg)", marginTop: 6 }}>
                    A supporting document is required for {leaveType} leave of more than {attachmentThreshold} day(s).
                  </p>
                )}
              </div>
            </div>

            {/* Drawer footer */}
            <div style={{ padding: "var(--space-4) var(--space-6)", borderTop: "1px solid var(--color-surface-200)", display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                onClick={closeDrawer}
                style={{ padding: "8px 18px", border: "1px solid var(--color-surface-200)", borderRadius: "var(--radius-md)", background: "none", cursor: "pointer", fontSize: 14 }}
              >
                Cancel
              </button>
              <AsyncButton
                onClick={() => handleApply()}
                disabled={saving || !leaveType || !startDate}
              >
                {editingId ? "Save Changes" : "Submit Request"}
              </AsyncButton>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
